/** @module csi/store/ValuePairs */
define([
	"dojo/_base/declare",
	"csi/_base/underscore",
	"csi/comm",
	"csi/heading/_base",
	"csi/xml",
	"csi/xml/Query",
	"./_RequestQueryMixin",
	"./Request",
	"./Values"
], function(declare, _, comm, csiHeading, xml, Query, _RequestQueryMixin, RequestStore) {

	function _addSpecParams(/*Request*/req, /*Spec*/spec, /*String*/suffix) {
		if (!spec) {
			return;
		}

		var qs = spec.querySpec;
		var id = qs.id;
		var index = qs.index;

		if (id) {
			req.addParameter("Qry.Value.Attr" + suffix, id);
		} else {
			req.addParameter("Qry.Value.Node" + suffix, xml.propertyToFilterParameter(qs.prop));
		}

		req.addParameter("Qry.Value.Index" + suffix, index);
		req.addParameter("Qry.Value.Path" + suffix, qs.path);
	}

	var _ValuePairs = declare([RequestStore, _RequestQueryMixin], {
		queryClass: Query,
		requestBinaryParameter: comm.QUERY_XML,
		requestModule: comm.MOD_SEARCH,
		requestOperation: "ValuePairsByXML",

		// item properties
		idProperty: "id",
		countProperty: "Count",
		value1Property: "Value",
		value2Property: "Value2",
		refsProperty: "Refs",

		/** @property {boolean} bHasRefs - indicates if store items contains refs (urls) for items per value */
		//bHasRefs: false,

		// String[]: parameters in query options that do not apply to querying for values (such as those for paging)
		requestOmitOptions: ["count", "direction", "noActionData", "queryOptions", "start", "store"],

		// String[]: omit properties client might filter by
		requestOmitQuery: ["id", "Value", "Value2"],

		// String[]: parameters to omit from Values request
		requestOmitParameters: [
			"Crew.Scope",
			"Dep.Child.Attr", "Dep.Child.Level", "Dep.Parent.Attr", "Dep.Parent.Level", "Dep.Path",
			"Fmt.AC.Policies", "Fmt.AC.Rights", "Fmt.Attr.Info", "Fmt.Attr.List", "Fmt.Complete", "Fmt.Complete.Max", "Fmt.Crew", "Fmt.Node.Info"
		],

		// Spec?: specifications for pair of properties/attributes
		//spec1: null,
		//spec2: null,

		/**
		 * Add item given the values
		 * @param {any} value1
		 * @param {any} value2
		 * @param {string} [type1=string]
		 * @param {string} [type2=string]
		 * @param {number} [count=1]
		 */
		addByValue: function(value1, value2, type1, type2, count) {
			const id = this._getId(value1, value2);
			var item = this.get(id);

			count = count || 1;

			if (item) {
				item.Count += count;
			} else {
				item = {
					id: id,
					Count: count,
					Refs: [],
					Type: type1 || xml.ATTR_STRING,
					Type2: type2 || xml.ATTR_STRING,
					Value: value1,
					Value2: value2
				};

				this.add(item);
			}

			return item;
		},

		/**
		 * Return item corresponding to given values
		 * @param {any} value1
		 * @param {any} value2
		 * @return {Object}
		 */
		getByValue: function(value1, value2) {
			return this.get(this._getId(value1, value2));
		},

		getRequest: function(/*Object?*/query, /*Object?*/options, /*Boolean?*/bReport, /*Boolean?*/bForce) {
			// summary: add parameters based on specs
			var request = this.inherited(arguments);
			if (request) {
				_addSpecParams(request, this.spec1, "");
				_addSpecParams(request, this.spec2, "2");
			}

			return request;
		},

		getResultTotal: function(/*Number?*/nDisplayed) {
			// summary: iterate through results, adding counts along the way
			var data = this._store.data;
			var n = nDisplayed || data.length;
			var count = 0;

			for (var i = 0; i < n; ++i) {
				count += data[i].Count;
			}

			return count;
		},

		/**
		 * Remove reference to result with given id
		 * @param {string} resultId
		 * @return {Object} - return item if reference to result is found and removed
		 */
		removeRef: function(resultId) {
			if (!this.isLoaded()) {
				return null;
			}

			const {countProperty, refsProperty} = this;
			const data = this._store.data;

			return data.find(item => {
				const refs = item[refsProperty];
				const n = _.indexOf(refs, resultId);

				if (n >= 0) {
					refs.splice(n, 1);
					--item[countProperty];
					return true;
				}
			});
		},

		/**
		 * Convert given value pairs or nodes to store items
		 * @param {Object[]} resultValuePairs
		 * @param {Node[]} resultNodes
		 */
		_convertToData: function(resultValuePairs, resultNodes) {
			const ctx = this.ctx;
			const mapItems = {};

			var valuePairs = resultValuePairs;
			if (!valuePairs) {
				var spec1 = this.spec1;
				var spec2 = this.spec2;

				if (!resultNodes || !spec1 || !spec2 || !ctx) {
					valuePairs = [];
				} else {
					var h1 = spec1.heading;
					var h2 = spec2.heading;
					var pairMap = {};

					this.bHasRefs = true;

					ctx.forEach(resultNodes, function(ctx) {
						var value1 = csiHeading.toFilterValue(ctx, h1);
						var value2 = csiHeading.toFilterValue(ctx, h2);
						var id = this._getId(value1, value2);
						var obj = pairMap[id];

						if (!obj) {
							obj = pairMap[id] = {
								Count: 0,
								Refs: [],
								Type: spec1.type,
								Type2: spec2.type,
								Value: value1,
								Value2: value2
							};
						}

						++obj.Count;
						obj.Refs.push(ctx.getNodeURL());
					}, this);

					valuePairs = _.values(pairMap);
				}
			}

			// convert valuePairs to data items
			const data = _.filter(valuePairs, function(item) {
				// treat null and empty string values as the same
				if (item.Value == null) {
					item.Type = xml.ATTR_STRING;
					item.Value = "";
				}

				if (item.Value2 == null) {
					item.Type2 = xml.ATTR_STRING;
					item.Value2 = "";
				}

				item.id = this._getId(item.Value, item.Value2);
				item.Count = Number(item.Count);

				var item2 = mapItems[item.id];
				if (item2) {
					item2.Count += item.Count;

					if (item.Refs) {
						item2.Refs = item2.Refs.concat(item.Refs);
					}

					return false;
				}

				mapItems[item.id] = item;
				return true;
			}, this);

			this.bHasRefs = !!(valuePairs[0] && valuePairs[0].Refs);
			this._setData(data);
		},

		/**
		 * Return identity for item given pair of values
		 * @param {any} v1
		 * @param {any} v2
		 * @return {string}
		 */
		_getId: function(v1, v2) {
			const spec1 = this.spec1;
			const spec2 = this.spec2;

			if (spec1 && (spec1.type == xml.ATTR_ENUM) && !xml.enumGetText(v1)) {
				v1 = null;
			}

			if (spec2 && (spec2.type == xml.ATTR_ENUM) && !xml.enumGetText(v2)) {
				v2 = null;
			}

			v1 = (v1 == null) ? "" : v1.toString();
			v2 = (v2 == null) ? "" : v2.toString();

			return (v1 || xml.VALUE_BLANK) + "@@@@" + (v2 || xml.VALUE_BLANK);
		},

		_onResponse: function(/*Response*/res, /*Request*/req, /*Object*/options) {
			// summary: set pairs as data after assigning unique id

			// cache any nodes returned, which can happen if getting ref values
			const ctx = this.ctx;
			if (ctx) {
				ctx.cache.cacheResponse(res);
			}

			this._convertToData(res.getValuePairs(), res.getResultNodes());
		},

		_onStoreOptions: function(/*Object*/options) {
			// summary: add blank value
			if (options.requestParameters && this.requestOmitParameters) {
				options.requestParameters = _.omit(options.requestParameters, this.requestOmitParameters);
			}
		}
	});

	_ValuePairs.createFromNodes = function(/*Node[]*/nodes, /*Object*/params) {
		// summary: create ValuesStore from list of nodes
		var store = new _ValuePairs(params);

		console.assert(!!params.ctx && !!params.spec1 && !!params.spec2);

		store.requestModule = null;
		store._convertToData(null, nodes);
		return store;
	};

	return _ValuePairs;
});